/**
 * Animation Controller Utility
 * Manages animation states and transitions for notifications
 */

import { AnimationState, AnimationConfig } from '../types';

/**
 * Default animation configuration
 */
export const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
  duration: {
    fast: 200,
    moderate: 300,
    slow: 500,
  },
  easing: {
    enter: 'cubic-bezier(0.4, 0, 0.2, 1)', // ease-out
    exit: 'cubic-bezier(0.4, 0, 1, 1)', // ease-in
    reposition: 'cubic-bezier(0.4, 0, 0.2, 1)', // ease-in-out
  },
  distance: {
    slideIn: '2rem',
    slideOut: '1rem',
  },
};

/**
 * Animation state machine transitions
 */
const VALID_TRANSITIONS: Record<AnimationState, AnimationState[]> = {
  entering: ['idle', 'exiting'],
  idle: ['exiting'],
  exiting: ['exited'],
  exited: ['entering'],
};

/**
 * Check if a state transition is valid
 */
export const isValidTransition = (
  from: AnimationState,
  to: AnimationState
): boolean => {
  return VALID_TRANSITIONS[from]?.includes(to) || false;
};

/**
 * Get animation duration based on state and preference
 */
export const getAnimationDuration = (
  state: AnimationState,
  speed: 'fast' | 'moderate' | 'slow' = 'moderate',
  config: AnimationConfig = DEFAULT_ANIMATION_CONFIG
): number => {
  if (state === 'entering' || state === 'exiting') {
    return config.duration[speed];
  }
  return 0;
};

/**
 * Get easing function for animation state
 */
export const getAnimationEasing = (
  state: AnimationState,
  config: AnimationConfig = DEFAULT_ANIMATION_CONFIG
): string => {
  switch (state) {
    case 'entering':
      return config.easing.enter;
    case 'exiting':
      return config.easing.exit;
    default:
      return config.easing.reposition;
  }
};

/**
 * Create CSS transition string
 */
export const createTransition = (
  properties: string[],
  duration: number,
  easing: string,
  delay: number = 0
): string => {
  const delayStr = delay > 0 ? ` ${delay}ms` : '';
  return properties
    .map(prop => `${prop} ${duration}ms ${easing}${delayStr}`)
    .join(', ');
};

/**
 * Get staggered delay for multiple notifications
 */
export const getStaggerDelay = (
  index: number,
  baseDelay: number = 50,
  maxDelay: number = 200
): number => {
  return Math.min(index * baseDelay, maxDelay);
};

/**
 * Create keyframe animations
 */
export const createKeyframes = (name: string): string => {
  switch (name) {
    case 'notificationSlideInRight':
      return `
        @keyframes notificationSlideInRight {
          from {
            transform: translateX(calc(100% + 2rem));
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `;
    case 'notificationSlideInLeft':
      return `
        @keyframes notificationSlideInLeft {
          from {
            transform: translateX(calc(-100% - 2rem));
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `;
    case 'notificationSlideInTop':
      return `
        @keyframes notificationSlideInTop {
          from {
            transform: translateY(-2rem);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `;
    case 'notificationSlideInBottom':
      return `
        @keyframes notificationSlideInBottom {
          from {
            transform: translateY(2rem);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `;
    case 'notificationFadeOut':
      return `
        @keyframes notificationFadeOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.95);
          }
        }
      `;
    case 'notificationShake':
      return `
        @keyframes notificationShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
      `;
    case 'notificationPulse':
      return `
        @keyframes notificationPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
      `;
    default:
      return '';
  }
};

/**
 * Animation class for managing notification animations
 */
export class AnimationController {
  private config: AnimationConfig;
  private respectReducedMotion: boolean;
  private animationFrameId: number | null = null;

  constructor(
    config: AnimationConfig = DEFAULT_ANIMATION_CONFIG,
    respectReducedMotion: boolean = true
  ) {
    this.config = config;
    this.respectReducedMotion = respectReducedMotion;
  }

  /**
   * Check if animations should be disabled
   */
  shouldDisableAnimations(): boolean {
    if (!this.respectReducedMotion) {
      return false;
    }
    
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    
    return false;
  }

  /**
   * Animate a state transition
   */
  async animateTransition(
    from: AnimationState,
    to: AnimationState,
    duration?: number
  ): Promise<void> {
    if (!isValidTransition(from, to)) {
      throw new Error(`Invalid transition from ${from} to ${to}`);
    }

    if (this.shouldDisableAnimations()) {
      return Promise.resolve();
    }

    const animDuration = duration || this.config.duration.moderate;

    return new Promise(resolve => {
      setTimeout(resolve, animDuration);
    });
  }

  /**
   * Request animation frame with cleanup
   */
  requestFrame(callback: () => void): void {
    this.cancelFrame();
    this.animationFrameId = requestAnimationFrame(callback);
  }

  /**
   * Cancel animation frame
   */
  cancelFrame(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Batch DOM updates for better performance
   */
  batchUpdate(updates: (() => void)[]): void {
    this.requestFrame(() => {
      updates.forEach(update => update());
    });
  }

  /**
   * Get animation CSS classes based on state
   */
  getAnimationClasses(
    state: AnimationState,
    position: string
  ): string[] {
    const classes: string[] = [`notification--${state}`];

    if (state === 'entering') {
      if (position.includes('right')) {
        classes.push('notification--slide-in-right');
      } else if (position.includes('left')) {
        classes.push('notification--slide-in-left');
      } else if (position.includes('top')) {
        classes.push('notification--slide-in-top');
      } else {
        classes.push('notification--slide-in-bottom');
      }
    } else if (state === 'exiting') {
      classes.push('notification--fade-out');
    }

    return classes;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<AnimationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.cancelFrame();
  }
}

/**
 * Create animation controller instance
 */
export const createAnimationController = (
  config?: Partial<AnimationConfig>,
  respectReducedMotion?: boolean
): AnimationController => {
  return new AnimationController(
    { ...DEFAULT_ANIMATION_CONFIG, ...config },
    respectReducedMotion
  );
};

/**
 * Generate animation token CSS variables
 */
export const generateAnimationTokens = (): string => {
  return `
    :root {
      /* Duration tokens */
      --notification-animation-fast: 200ms;
      --notification-animation-moderate: 300ms;
      --notification-animation-slow: 500ms;
      
      /* Easing tokens */
      --notification-easing-enter: cubic-bezier(0.4, 0, 0.2, 1);
      --notification-easing-exit: cubic-bezier(0.4, 0, 1, 1);
      --notification-easing-reposition: cubic-bezier(0.4, 0, 0.2, 1);
      
      /* Distance tokens */
      --notification-slide-distance: 2rem;
      --notification-slide-distance-small: 1rem;
      
      /* Stagger tokens */
      --notification-stagger-delay: 50ms;
      --notification-stagger-max-delay: 200ms;
    }
    
    @media (prefers-reduced-motion: reduce) {
      :root {
        --notification-animation-fast: 0ms;
        --notification-animation-moderate: 0ms;
        --notification-animation-slow: 0ms;
      }
    }
  `;
};
